import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { ProductsDropdown } from './ProductsDropdown';

export const MarketingNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('es');
  const languageRef = useRef(null);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigation = [
    { name: 'Inicio', href: '/' },
    { name: 'Documentación', href: '/documentation' },
    { name: 'Contacto', href: '/contact' },
  ];

  const languages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <div className="h-8 w-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">D</span>
              </div>
              <span className="ml-2 text-xl font-bold text-gray-900">DICOMITY</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {/* Inicio Link */}
              <Link
                to="/"
                className="text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
              >
                Inicio
              </Link>
              
              {/* Products Dropdown */}
              <ProductsDropdown />
              
              {/* Rest of navigation */}
              {navigation.slice(1).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Language Selector and CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Dropdown */}
            <div className="relative" ref={languageRef}>
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
              >
                <GlobeAltIcon className="h-4 w-4 mr-1" />
                {languages.find(lang => lang.code === selectedLanguage)?.flag}
                <ChevronDownIcon className="h-3 w-3 ml-1" />
              </button>

              {/* Language Dropdown Menu */}
              {isLanguageOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => {
                          setSelectedLanguage(language.code);
                          setIsLanguageOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 flex items-center ${
                          selectedLanguage === language.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                        }`}
                      >
                        <span className="mr-3">{language.flag}</span>
                        {language.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <Link
              to="/login"
              className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-600 transition-colors duration-200"
            >
              Acceder
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
            {/* Inicio Link */}
            <Link
              to="/"
              className="text-gray-600 hover:text-blue-600 block px-3 py-2 text-base font-medium transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
              onClick={() => setIsMenuOpen(false)}
            >
              Inicio
            </Link>
            
            {/* Mobile Products Section */}
            <div className="px-3 py-2">
              <div className="text-gray-500 text-sm font-medium mb-2">Productos</div>
              <div className="space-y-1 ml-4">
                <Link
                  to="/dicomfiles"
                  className="text-gray-600 hover:text-blue-600 block px-3 py-2 text-sm transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
                  onClick={() => setIsMenuOpen(false)}
                >
                  DICOMFiles
                </Link>
                <Link
                  to="/dicomcapture"
                  className="text-gray-600 hover:text-blue-600 block px-3 py-2 text-sm transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
                  onClick={() => setIsMenuOpen(false)}
                >
                  DICOMCapture
                </Link>
                <Link
                  to="/products"
                  className="text-gray-600 hover:text-blue-600 block px-3 py-2 text-sm transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Ver todos
                </Link>
              </div>
            </div>
            
            {/* Rest of navigation */}
            {navigation.slice(1).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-600 hover:text-blue-600 block px-3 py-2 text-base font-medium transition-all duration-200 border-b-2 border-transparent hover:border-blue-600"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Mobile Language Selector */}
            <div className="px-3 py-2 mt-4 border-t border-gray-200">
              <div className="text-gray-500 text-sm font-medium mb-2">Idioma</div>
              <div className="space-y-1 ml-4">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => {
                      setSelectedLanguage(language.code);
                      setIsMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-all duration-200 border-b-2 border-transparent hover:border-blue-600 flex items-center ${
                      selectedLanguage === language.code 
                        ? 'text-blue-600 border-blue-600' 
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    <span className="mr-3">{language.flag}</span>
                    {language.name}
                  </button>
                ))}
              </div>
            </div>
            
            <Link
              to="/login"
              className="bg-blue-500 text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-600 transition-colors duration-200 mt-4"
              onClick={() => setIsMenuOpen(false)}
            >
              Acceder
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};
